import CasesApi from '../cases'
import { IDatahubCase } from '../cases/models'
import http, { endpoints } from '../http'
import {
	IBookingInfoDto,
	ICreateOrderResponseDto,
	IDatahubOrderDto,
	IDatahubOrderLineDto,
	IOrderLineAction,
	IPropertyTypeDto,
	IPublicBookingDto,
	ISimplifiedOrderDto,
	IUpdateOrderResponsibleDto,
} from './dtos'
import {
	mapBookingInfoFromDto,
	mapDatahubOrderDtoToSimplifiedOrder,
	mapDatahubOrderLineFromDto,
	mapFeedbackParamsToDto,
	mapPublicBookingFromDto,
	mapSimplifiedOrderFromDto,
} from './helpers'
import {
	ICreateOrderParams,
	ICreateOrderResponse,
	IFeedbackParams,
	IUpdateOrderParams,
	IUpdateWatchersParams,
	ICreateOrderChoice,
	ISimplifiedOrder,
} from './models'
import { LocalizedError } from 'localization'
import GoogleApi from '../google'
import TasksApi from '../tasks'
import { IDatahubWatcherDto } from '../users/dtos'
import { ICommonResponse } from '../types'

const datahubClient = http.createClient(endpoints.datahub)

export default class OrdersApi {
	static async getPropertyTypes() {
		const response = await datahubClient.get<IPropertyTypeDto[]>('/property-types')

		return response?.data ? response.data : []
	}

	static async submitFeedback(params: IFeedbackParams) {
		const response = await datahubClient.post<ICommonResponse>('/orders/rating/w2', mapFeedbackParamsToDto(params))
		return response?.data
	}

	static async createOrder({
		case: caseObj,
		address = {},
		contactPersons,
		property,
		package: packageObj,
		products,
		keyPickupAddress,
		responsibleUser,
		watchers,
		hasExternalStylist,
		photographerComment,
		endUserInvoice,
		electronicInvoice,
		departmentId,
		branchId,
		anyTaskIncludesBooking,
		orderId,
	}: ICreateOrderParams): Promise<ICreateOrderResponse> {
		const caseId = await CasesApi.upsertCase({
			case: caseObj,
			address,
			property,
			contactPersons,
			branchId,
		})

		const response = await datahubClient.post<ICreateOrderResponseDto>('/orders/new/webshop', {
			caseId,
			orderId,
			bookingInfo: anyTaskIncludesBooking
				? {
						bookingRequested: anyTaskIncludesBooking,
						address: {
							street: address.street,
							buildingNumber: address.houseNumber,
							buildingFloor: address.floor,
							buildingDoor: address.appartmentNumber,
							cityArea: address.area,
							city: address.city,
							zip: address.postNumber,
						},
						pickUpKeyRequested: !!keyPickupAddress,
						pickUpKeyAddress: {
							street: keyPickupAddress?.street,
							buildingNumber: keyPickupAddress?.houseNumber,
							buildingFloor: keyPickupAddress?.floor,
							buildingDoor: keyPickupAddress?.appartmentNumber,
							cityArea: keyPickupAddress?.area,
							city: keyPickupAddress?.city,
							zip: keyPickupAddress?.postNumber,
						},
						commentForPhotographers: photographerComment,
						contactPersons:
							// hack to safe broken data
							contactPersons
								?.filter(item => !!item)
								.map(contactPerson => ({
									contactType: contactPerson.type,
									firstName: contactPerson.name.split(' ')?.[0],
									lastName: contactPerson.name.split(' ')?.[1],
									phone: contactPerson.phone,
									alternativePhone: contactPerson.phoneAlt,
									email: contactPerson.email,
								})) || [],
						salesStylingRequested: hasExternalStylist,
				  }
				: null,
			propertyInfo: property?.livingArea
				? {
						basementArea: property?.basementArea,
						registeredArea: property?.livingArea,
						totalStoreys: property?.floors,
				  }
				: null,
			responsibleEmail: responsibleUser?.email,
			package: packageObj?.id,
			productChoices: packageObj?.choices?.reduce(
				(memo, { group, selectedProductId, selectedPackageId, products: productChoices, packages: packageChoices }) => {
					if (selectedPackageId && packageChoices) {
						const foundPackage = packageChoices.find(({ id }) => id === selectedPackageId)
						if (foundPackage) {
							memo.push({
								itemId: foundPackage.itemNumber,
								config: foundPackage.config,
								group,
								quantity: 1,
							})
						}
					} else if (!Array.isArray(selectedProductId) && productChoices) {
						const foundProduct = productChoices.find(({ id }) => id === selectedProductId)
						if (foundProduct) {
							memo.push({
								itemId: foundProduct.itemNumber,
								config: foundProduct.config,
								group,
								quantity: foundProduct.quantity,
							})
						}
					}

					return memo
				},
				[] as ICreateOrderChoice[]
			),
			products:
				products?.map(({ id, quantity }) => ({
					productId: id,
					quantity,
				})) || [],
			invoicing: {
				endUserInvoicing: endUserInvoice
					? {
							requested: !!endUserInvoice,
							address: {
								street: endUserInvoice?.address.street,
								buildingNumber: endUserInvoice?.address.houseNumber,
								buildingFloor: endUserInvoice?.address.floor,
								buildingDoor: endUserInvoice?.address.appartmentNumber,
								cityArea: endUserInvoice?.address.area,
								city: endUserInvoice?.address.city,
								zip: endUserInvoice?.address.postNumber,
							},
							firstName: endUserInvoice?.firstName,
							lastName: endUserInvoice?.lastName,
							email: endUserInvoice?.email,
							phone: endUserInvoice?.phone,
					  }
					: null,
				businessInvoicing: electronicInvoice?.cvr
					? {
							requested: !!electronicInvoice,
							address: {
								street: electronicInvoice?.address.street,
								buildingNumber: electronicInvoice?.address.houseNumber,
								buildingFloor: electronicInvoice?.address.floor,
								buildingDoor: electronicInvoice?.address.appartmentNumber,
								cityArea: electronicInvoice?.address.area,
								city: electronicInvoice?.address.city,
								zip: electronicInvoice?.address.postNumber,
							},
							firstName: electronicInvoice?.name,
							email: electronicInvoice?.email,
							phone: electronicInvoice?.phone,
							vat: electronicInvoice?.cvr,
							ean: electronicInvoice.ean,
							comment: electronicInvoice?.comment,
					  }
					: null,
			},
			watcherEmails: watchers?.map(item => item?.email) || [],
		})

		const creationResponse = response?.data || {}

		if (!creationResponse?.success) {
			throw new LocalizedError('orders.pages.new.createOrderFailed')
		}

		if (import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID) {
			await GoogleApi.sendOrderCompleted(departmentId, caseObj.reference, creationResponse.orderId)
		}

		const tasks = (await TasksApi.getOrderTasks(creationResponse.orderId)) || []

		return {
			salesId: creationResponse.salesId,
			tasks,
			caseId,
			orderId: creationResponse.orderId,
		}
	}

	static async updateResponsibleAgent(params: IUpdateOrderResponsibleDto) {
		const response = await datahubClient.post<ICommonResponse>(`/orders/${params.orderId}/responsible`, {
			userId: params.userId,
		})

		if (!response?.data.success) {
			throw new LocalizedError('orders.pages.details.responsibleUpdateError')
		}

		return response?.data
	}

	static async updateOrderWatchers(params: IUpdateWatchersParams) {
		const response = await datahubClient.post<boolean>(`/orders/${params.orderId}/watchers`, {
			id: params.orderId,
			userIds: params.userIds,
		})

		if (!response?.data) {
			throw new LocalizedError('orders.pages.details.watchersUpdateError')
		}

		return response?.data
	}

	static async getBookingById(bookingId: string) {
		const response = await datahubClient.get<IPublicBookingDto>(`/Bookings/${bookingId}`)
		return response?.data ? mapPublicBookingFromDto(response.data) : undefined
	}

	static async getOrderLines(orderId: ISimplifiedOrder['id']) {
		const response = await datahubClient.get<IDatahubOrderLineDto[]>(`/orders/${orderId}/orderlines`)

		return response?.data ? response?.data?.map(mapDatahubOrderLineFromDto) : []
	}

	static async getOrderLineActions(orderlineIds: IDatahubOrderLineDto['id'][]) {
		const response = await datahubClient.get<IOrderLineAction[]>(`/orders/orderlines/actions`, {
			params: {
				orderlineIds,
			},
		})

		return response?.data || []
	}

	static async addOrderLines(params: IUpdateOrderParams) {
		const response = await datahubClient.post<{ success: boolean }[]>(
			`/orders/${params.orderId}/orderlines/add`,
			params.caseProducts.map(({ quantity, itemNumber, config, variant }) => ({
				itemId: itemNumber,
				config,
				quantity,
				variant,
			}))
		)

		if (response?.data?.some(({ success }) => !success)) {
			throw new LocalizedError('modals.addExtraOrderLine.orderUpdateError')
		}
	}

	static async getCaseOrders(id: IDatahubCase['id']) {
		const response = await datahubClient.get<ISimplifiedOrderDto[]>(`/cases/${id}/orders`)
		return response?.data ? response.data.map(mapSimplifiedOrderFromDto) : []
	}

	static async getDatahubOrderById(orderId: ISimplifiedOrder['id']) {
		const response = await datahubClient.get<IDatahubOrderDto>(`/orders/${orderId}`)
		return response?.data ? mapDatahubOrderDtoToSimplifiedOrder(response.data) : undefined
	}

	static async getOrderWatchers(orderId: string) {
		const response = await datahubClient.get<IDatahubWatcherDto[]>(`/orders/${orderId}/watchers`)

		return response?.data || []
	}

	static async getBookingInfo(orderId: ISimplifiedOrder['id']) {
		const response = await datahubClient.get<IBookingInfoDto>(`/orders/${orderId}/booking-info`)

		return response?.data ? mapBookingInfoFromDto(response.data) : undefined
	}
}
